import { useEffect, useRef } from 'react';

/**
 * Značky a atributy, které pustíme dál. Text píše vedoucí dílny, ale HTML
 * putuje do databáze a zpátky do tisku — čistíme ho proto při každém průchodu,
 * ne jen při ukládání.
 */
const ALLOWED_TAGS = new Set([
  'P',
  'DIV',
  'BR',
  'B',
  'STRONG',
  'I',
  'EM',
  'U',
  'UL',
  'OL',
  'LI',
  'SPAN',
]);
const ALLOWED_ALIGN = new Set(['left', 'center', 'right', 'justify']);

/** Vyčistí HTML z editoru — povolené značky, z atributů jen zarovnání. */
export function sanitizeHtml(html: string): string {
  // Starší poznámky jsou prostý text — zachovej odřádkování a nic víc.
  if (!html.includes('<')) {
    return html
      .split('\n')
      .map((line) => `<p>${escapeHtml(line) || '<br>'}</p>`)
      .join('');
  }
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const walk = (node: Element) => {
    for (const child of [...node.children]) {
      if (!ALLOWED_TAGS.has(child.tagName)) {
        // Neznámou značku zahodíme, ale text uvnitř zachováme.
        child.replaceWith(...child.childNodes);
        continue;
      }
      const align = child.getAttribute('style')?.match(/text-align:\s*(\w+)/)?.[1] ?? '';
      for (const attr of [...child.attributes]) child.removeAttribute(attr.name);
      if (ALLOWED_ALIGN.has(align)) child.setAttribute('style', `text-align: ${align}`);
      walk(child);
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

const BTN =
  'h-8 min-w-8 px-2 rounded-md bg-action text-white text-sm font-bold hover:bg-action-dark cursor-pointer';

/**
 * Malý WYSIWYG editor (tučné, kurzíva, podtržení, zarovnání, seznamy).
 * Staví na `contentEditable` a `execCommand` — pro pár formátovacích tlačítek
 * je to levnější než tahat do projektu celou editorovou knihovnu.
 */
export function RichTextEditor({
  value,
  onChange,
  rows = 8,
}: {
  value: string;
  onChange: (html: string) => void;
  rows?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Obsah nastavujeme jen zvenčí (načtení dat) — jinak by kurzor skákal na začátek.
  const lastSet = useRef<string>('');

  useEffect(() => {
    const el = ref.current;
    if (!el || value === lastSet.current) return;
    el.innerHTML = sanitizeHtml(value);
    lastSet.current = value;
  }, [value]);

  function exec(command: string, arg?: string) {
    ref.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  }

  function emit() {
    const html = ref.current?.innerHTML ?? '';
    lastSet.current = html;
    onChange(html);
  }

  return (
    <div className="rounded-[5px] border border-[#cbd5e1] overflow-hidden">
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border-light bg-bg px-2 py-1.5">
        <button type="button" onClick={() => exec('undo')} title="Zpět" className={BTN}>
          ↶
        </button>
        <button type="button" onClick={() => exec('redo')} title="Vpřed" className={BTN}>
          ↷
        </button>
        <span className="w-2" />
        <button type="button" onClick={() => exec('bold')} title="Tučně" className={BTN}>
          B
        </button>
        <button
          type="button"
          onClick={() => exec('italic')}
          title="Kurzíva"
          className={`${BTN} italic`}
        >
          I
        </button>
        <button
          type="button"
          onClick={() => exec('underline')}
          title="Podtrženě"
          className={`${BTN} underline`}
        >
          U
        </button>
        <span className="w-2" />
        <button type="button" onClick={() => exec('justifyLeft')} title="Vlevo" className={BTN}>
          ≡
        </button>
        <button
          type="button"
          onClick={() => exec('justifyCenter')}
          title="Na střed"
          className={BTN}
        >
          ⋯
        </button>
        <button type="button" onClick={() => exec('justifyRight')} title="Vpravo" className={BTN}>
          ≣
        </button>
        <span className="w-2" />
        <button
          type="button"
          onClick={() => exec('insertUnorderedList')}
          title="Odrážky"
          className={BTN}
        >
          •
        </button>
        <button
          type="button"
          onClick={() => exec('insertOrderedList')}
          title="Číslovaný seznam"
          className={BTN}
        >
          1.
        </button>
      </div>
      {/* biome-ignore lint/a11y/useFocusableInteractive: contentEditable je fokusovatelný sám */}
      <div
        ref={ref}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label="Poznámky"
        onInput={emit}
        onBlur={emit}
        style={{ minHeight: `${rows * 1.6}rem` }}
        className="px-3 py-2 text-sm text-text bg-white focus:outline-none [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-6 [&_ol]:pl-6"
      />
    </div>
  );
}

/** Jen zobrazení uloženého HTML (tisková sestava) — bez editace. */
export function RichTextView({ html, className }: { html: string; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = sanitizeHtml(html);
  }, [html]);
  return <div ref={ref} className={className} />;
}
